import { Check, LoaderCircle, TriangleAlert } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { AddressSuggestion, DispatchClient, PlaceDetail } from '../../../api/dispatch';
import { ApiError } from '../../../api/envelope';
import type { LegState } from './bookingForm';

/**
 * One booking leg: free text (usually the AI's extraction) that must become a
 * dispatch place. Typing re-searches dispatch's Google proxy into a suggestion
 * menu; picking a row locks in coordinates. The box itself does the initial
 * silent resolve — this field only handles the human correction loop.
 */

const formatDetail = (detail: PlaceDetail): string =>
  [detail.addressLine1, detail.city, detail.state].filter(Boolean).join(', ');

export function AddressField({
  label,
  leg,
  applyOn,
  dispatch,
  missing = false,
  onChange,
}: {
  label: string;
  leg: LegState;
  /** Set for pickup/dropoff → detail/v2, which also zone-checks. */
  applyOn?: 'O' | 'H';
  dispatch: DispatchClient;
  /** A blocked save named this leg — tint the input like the other gaps. */
  missing?: boolean;
  onChange: (leg: LegState) => void;
}) {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[] | null>(null);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // One Google billing session per search→pick pair, like the dashboard.
  const sessionRef = useRef<string>(dispatch.newSessionToken());
  const requestSeq = useRef(0);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  const search = (text: string) => {
    const seq = ++requestSeq.current;
    setSearching(true);
    dispatch
      .searchPlaces(text, sessionRef.current)
      .then((rows) => {
        if (seq !== requestSeq.current) return;
        setSuggestions(rows.slice(0, 5));
        setSearching(false);
      })
      .catch(() => {
        if (seq !== requestSeq.current) return;
        setSuggestions([]);
        setSearching(false);
      });
  };

  const handleInput = (text: string) => {
    // Any edit invalidates the previous resolution — coordinates must never
    // outlive the text they were resolved from.
    onChange({ text, resolving: false, resolved: null, matchedText: null, error: null });
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!text.trim()) {
      setSuggestions(null);
      return;
    }
    debounceRef.current = setTimeout(() => search(text), 300);
  };

  const pick = async (suggestion: AddressSuggestion) => {
    setSuggestions(null);
    onChange({ ...leg, text: suggestion.text, resolving: true, resolved: null, error: null });
    try {
      const detail = applyOn
        ? await dispatch.getPlaceDetailV2(suggestion.placeId, sessionRef.current, applyOn)
        : await dispatch.getPlaceDetail(suggestion.placeId, sessionRef.current);
      sessionRef.current = dispatch.newSessionToken();
      onChange({
        text: suggestion.text,
        resolving: false,
        resolved: detail,
        matchedText: suggestion.text,
        error: null,
      });
    } catch (e) {
      sessionRef.current = dispatch.newSessionToken();
      // Zone rejections ("This pickup address is not serviceable") arrive as
      // dispatch validation errors — their message is written for humans.
      onChange({
        ...leg,
        text: suggestion.text,
        resolving: false,
        resolved: null,
        error: e instanceof ApiError ? e.message : 'Could not look up that address.',
      });
    }
  };

  return (
    <div className="aib-field aib-addr">
      <span className="aib-label">{label}</span>
      <input
        className={`aib-input${missing ? ' aib-miss' : ''}`}
        type="text"
        value={leg.text}
        placeholder="Start typing an address…"
        onChange={(e) => handleInput(e.target.value)}
        onBlur={() => setTimeout(() => setSuggestions(null), 200)}
      />
      {suggestions && suggestions.length > 0 && (
        <div className="aib-addr-menu">
          {suggestions.map((s) => (
            <button key={s.placeId} type="button" onMouseDown={() => void pick(s)}>
              {s.text}
            </button>
          ))}
        </div>
      )}
      {(leg.resolving || searching) && (
        <span className="aib-addr-status">
          <LoaderCircle size={11} className="spin-anim" /> {searching ? 'Searching…' : 'Resolving…'}
        </span>
      )}
      {!leg.resolving && !searching && leg.resolved && (
        <span className="aib-addr-status ok">
          <Check size={11} /> {formatDetail(leg.resolved) || leg.matchedText}
        </span>
      )}
      {!leg.resolving && !searching && !leg.resolved && leg.error && (
        <span className="aib-addr-status err">
          <TriangleAlert size={11} /> {leg.error}
        </span>
      )}
      {!leg.resolving && !searching && !leg.resolved && !leg.error && leg.text.trim() && (
        <span className="aib-addr-status">Unresolved — pick a suggestion for a full save</span>
      )}
    </div>
  );
}
